/**
 * Gestion EDT régulier — vue en grille horaire avec créneaux pré-câblés
 * selon le niveau, multi-classes (tronc commun), copier d'EDT, déplacer.
 */
import { useMemo, useState } from 'react';
import {
  CalendarDays, Plus, RotateCw, Loader2, Layers, Copy, Move, Pencil, Trash2,
} from 'lucide-react';
import {
  useListerEdtQuery, useDeleteEdtMutation, useGenererSeancesMutation,
  useHorairesStandardsQuery,
} from '../../services/vieScolaireApi';
import { useGetClassesQuery } from '../../services/polesOptionsApi';
import ModalEdt from './ModalEdt';
import ModalCopier from './ModalCopier';
import ModalDeplacer from './ModalDeplacer';

const JOURS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'] as const;
const JOURS_LABEL: Record<string, string> = {
  lundi: 'Lundi', mardi: 'Mardi', mercredi: 'Mercredi',
  jeudi: 'Jeudi', vendredi: 'Vendredi', samedi: 'Samedi',
};

type Niveau = 'L1' | 'L2' | 'L3';

function niveauDeClasse(code: string): Niveau | null {
  const m = code.match(/^L([123])/i);
  return m ? (`L${m[1]}` as Niveau) : null;
}

export default function EdtSurveillance() {
  const [niveau, setNiveau] = useState<Niveau>('L1');
  const [periode, setPeriode] = useState<'pair' | 'impair'>('pair');
  const [classe, setClasse] = useState<string>('');
  const [editing, setEditing] = useState<any | null>(null);
  const [creerCreneau, setCreerCreneau] = useState<{ jour: string; heure_debut: string; heure_fin: string } | null>(null);
  const [copierSrc, setCopierSrc] = useState<any | null>(null);
  const [deplacerSrc, setDeplacerSrc] = useState<any | null>(null);

  const { data: classesAll } = useGetClassesQuery({ actif: true });
  const classesNiveau = useMemo(
    () => (classesAll || []).filter((c: any) => niveauDeClasse(c.code) === niveau),
    [classesAll, niveau],
  );

  // Si la classe sélectionnée ne correspond pas au niveau, on reset
  useMemo(() => {
    if (classe && niveauDeClasse(classe) !== niveau) setClasse('');
  }, [niveau, classe]);

  // Mapping niveau + période → semestre global (Matiere.semestre)
  const semestreGlobal = useMemo(() => {
    const map: Record<string, Record<string, number>> = {
      L1: { impair: 1, pair: 2 },
      L2: { impair: 3, pair: 4 },
      L3: { impair: 5, pair: 6 },
    };
    return map[niveau][periode];
  }, [niveau, periode]);

  const { data: edtData, isFetching, refetch } = useListerEdtQuery(
    classe ? { classe } : undefined,
  );

  // Filtre côté frontend par semestre (le model EDT a un champ `semestre` 1/2 local)
  const semestreLocal = periode === 'pair' ? 2 : 1;

  // Horaires standards : L1/L2 = 4 créneaux ; L3 = 2 (plus tard) ; samedi = L1/L2
  const { data: horL1 } = useHorairesStandardsQuery({ niveau: 'L1' });
  const { data: horL3 } = useHorairesStandardsQuery({ niveau: 'L3' });

  const creneauxStd = useMemo(() => {
    if (niveau === 'L3') {
      return horL3?.creneaux || [
        { heure_debut: '15:15', heure_fin: '17:15' },
        { heure_debut: '17:15', heure_fin: '19:15' },
      ];
    }
    return horL1?.creneaux || [
      { heure_debut: '08:00', heure_fin: '10:00' },
      { heure_debut: '10:15', heure_fin: '12:15' },
      { heure_debut: '13:00', heure_fin: '15:00' },
      { heure_debut: '15:15', heure_fin: '17:15' },
    ];
  }, [niveau, horL1, horL3]);

  // Pour le samedi : L3 utilise aussi les horaires L1/L2
  const creneauxSamedi = horL1?.creneaux || [
    { heure_debut: '08:00', heure_fin: '10:00' },
    { heure_debut: '10:15', heure_fin: '12:15' },
    { heure_debut: '13:00', heure_fin: '15:00' },
    { heure_debut: '15:15', heure_fin: '17:15' },
  ];

  // Lignes du tableau = union des créneaux semaine + samedi, triées.
  // Indispensable pour L3 : sinon, le samedi 08:00/10:15/13:00 n'ont pas de
  // ligne dans la grille → impossible de cliquer pour créer le créneau.
  const lignesGrille = useMemo(() => {
    const seen = new Set<string>();
    const items: Array<{ heure_debut: string; heure_fin: string }> = [];
    for (const c of [...creneauxStd, ...creneauxSamedi]) {
      if (seen.has(c.heure_debut)) continue;
      seen.add(c.heure_debut);
      items.push(c);
    }
    return items.sort((a, b) => a.heure_debut.localeCompare(b.heure_debut));
  }, [creneauxStd, creneauxSamedi]);

  const creneauxBruts = edtData?.results || [];
  // Filtre par semestre (les créneaux sans semestre = legacy, on les garde pour ne rien perdre)
  const creneaux = useMemo(
    () => creneauxBruts.filter((c: any) => !c.semestre || c.semestre === semestreLocal),
    [creneauxBruts, semestreLocal],
  );

  // Index : (jour, heure_debut) → créneau
  const parCellule = useMemo(() => {
    const m: Record<string, any> = {};
    for (const c of creneaux) {
      m[`${c.jour}|${c.heure_debut}`] = c;
    }
    return m;
  }, [creneaux]);

  const [delMut] = useDeleteEdtMutation();
  const [genererMut, { isLoading: generating }] = useGenererSeancesMutation();

  const handleDelete = async (c: any) => {
    const propage = c.tronc_commun_groupe && window.confirm(
      "Ce créneau fait partie d'un tronc commun. Supprimer SEULEMENT pour cette classe ? "
      + "(OK = seule cette classe / Annuler = abandonner)"
    );
    if (!c.tronc_commun_groupe && !window.confirm('Désactiver ce créneau ?')) return;
    if (c.tronc_commun_groupe && !propage) return;
    try { await delMut(c.id).unwrap(); refetch(); }
    catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
  };

  const handleGenerer = async () => {
    const res = await genererMut({ semaines: 4, classe: classe || undefined });
    if ('data' in res) {
      const d = res.data!;
      alert(`Séances générées : ${d.creees} nouvelles, ${d.existantes} déjà présentes.`);
    } else {
      alert('Erreur lors de la génération.');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
      {/* En-tête */}
      <header className="mb-4 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <CalendarDays size={22} /> Emplois du temps
          </h1>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
            Saisissez les créneaux hebdomadaires. Génèrent automatiquement les séances pour l'appel.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={handleGenerer} disabled={generating}
            className="px-3 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg inline-flex items-center gap-1.5">
            {generating ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />}
            Générer 4 semaines
          </button>
        </div>
      </header>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
          {(['L1', 'L2', 'L3'] as Niveau[]).map(n => (
            <button key={n} onClick={() => setNiveau(n)}
              className={`px-3 py-1.5 text-xs font-semibold rounded ${
                niveau === n
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700'
              }`}>
              {n}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
          {(() => {
            const semImpair = niveau === 'L1' ? 1 : niveau === 'L2' ? 3 : 5;
            const semPair   = niveau === 'L1' ? 2 : niveau === 'L2' ? 4 : 6;
            return [
              { v: 'impair' as const, label: `S${semImpair} (impair)` },
              { v: 'pair'   as const, label: `S${semPair} (pair)` },
            ];
          })().map(o => (
            <button key={o.v} onClick={() => setPeriode(o.v)}
              className={`px-3 py-1.5 text-xs font-semibold rounded ${
                periode === o.v
                  ? 'bg-emerald-600 text-white'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700'
              }`}>
              {o.label}
            </button>
          ))}
        </div>

        <select value={classe} onChange={(e) => setClasse(e.target.value)}
          className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
          <option value="">Toutes les classes {niveau}</option>
          {classesNiveau.map((c: any) => (
            <option key={c.code} value={c.code}>{c.code}</option>
          ))}
        </select>

        <button onClick={() => setCreerCreneau({ jour: 'lundi', heure_debut: '08:00', heure_fin: '10:00' })}
          className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg inline-flex items-center gap-1">
          <Plus size={12} /> Nouveau créneau
        </button>

        <button onClick={() => refetch()}
          className="px-2 py-1.5 text-xs bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300">
          {isFetching ? '…' : '↻'}
        </button>

        <span className="ml-auto text-xs text-gray-500">
          {creneaux.length} créneau(x){classe ? ` pour ${classe}` : ` ${niveau}`}
        </span>
      </div>

      {/* Si "Toutes" : prévenir l'utilisateur */}
      {!classe && (
        <div className="mb-3 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 rounded-lg text-xs">
          Sélectionnez une classe pour voir sa grille horaire. Les nouveaux créneaux peuvent toutefois être créés pour plusieurs classes en même temps.
        </div>
      )}

      {/* Grille horaire */}
      {classe && (
        <div className="overflow-x-auto -mx-3 sm:mx-0">
          <table className="w-full border-collapse text-sm min-w-[700px]">
            <thead>
              <tr>
                <th className="w-24 text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 px-2 py-2 text-left">Horaire</th>
                {JOURS.map(j => (
                  <th key={j} className="text-xs font-semibold text-gray-700 dark:text-gray-200 px-2 py-2 text-center border-b border-gray-200 dark:border-gray-700">
                    {JOURS_LABEL[j]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lignesGrille.map((cr) => (
                <tr key={cr.heure_debut}>
                  <td className="text-xs font-mono text-gray-600 dark:text-gray-400 px-2 py-2 align-top whitespace-nowrap">
                    {cr.heure_debut}<br />{cr.heure_fin}
                  </td>
                  {JOURS.map(j => {
                    // Samedi pour tous niveaux : créneaux L1/L2 (08:00 → 17:15).
                    // Lundi-vendredi : créneaux du niveau (L1/L2 = 4, L3 = 2 plus tardifs).
                    const horairesPourCeJour = j === 'samedi' ? creneauxSamedi : creneauxStd;
                    if (!horairesPourCeJour.find(h => h.heure_debut === cr.heure_debut)) {
                      return <td key={j} className="bg-gray-100/40 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700/50" />;
                    }
                    const c = parCellule[`${j}|${cr.heure_debut}`];
                    return (
                      <td key={j} className="border border-gray-200 dark:border-gray-700/50 align-top p-1.5 min-w-[130px]">
                        {c ? (
                          <CelluleRemplie c={c} onEdit={() => setEditing(c)}
                            onCopier={() => setCopierSrc(c)}
                            onDeplacer={() => setDeplacerSrc(c)}
                            onDelete={() => handleDelete(c)} />
                        ) : (
                          <button onClick={() => setCreerCreneau({ jour: j, heure_debut: cr.heure_debut, heure_fin: cr.heure_fin })}
                            className="w-full h-full min-h-[56px] flex items-center justify-center text-gray-300 dark:text-gray-600 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition">
                            <Plus size={14} />
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {niveau === 'L3' && (
                <tr>
                  <td className="text-[10px] text-gray-400 px-2 py-2 italic" colSpan={7}>
                    L3 : créneaux 15:15–19:15 du lundi au vendredi. Le samedi suit les horaires L1/L2 (08:00 → 17:15).
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Si pas de classe sélectionnée : vue compacte par jour */}
      {!classe && (
        <VueLibreParJour creneaux={creneaux} onEdit={setEditing} onCopier={setCopierSrc}
          onDeplacer={setDeplacerSrc} onDelete={handleDelete} />
      )}

      {/* Modals */}
      {(creerCreneau || editing) && (
        <ModalEdt
          editing={editing}
          presetCreneau={creerCreneau || undefined}
          classes={classesNiveau}
          classePreselectionnee={classe}
          niveau={niveau}
          periode={periode}
          onClose={() => { setEditing(null); setCreerCreneau(null); }}
          onSaved={() => { setEditing(null); setCreerCreneau(null); refetch(); }}
        />
      )}
      {copierSrc && (
        <ModalCopier
          source={copierSrc}
          classesDisponibles={classesNiveau.map((c: any) => c.code)
            .filter((code: string) => code !== copierSrc.classe)}
          onClose={() => setCopierSrc(null)}
          onCopied={() => { setCopierSrc(null); refetch(); }}
        />
      )}
      {deplacerSrc && (
        <ModalDeplacer
          source={deplacerSrc}
          creneauxStd={creneauxStd}
          onClose={() => setDeplacerSrc(null)}
          onMoved={() => { setDeplacerSrc(null); refetch(); }}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────

function CelluleRemplie({ c, onEdit, onCopier, onDeplacer, onDelete }: {
  c: any; onEdit: () => void; onCopier: () => void; onDeplacer: () => void; onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      className="relative bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded p-1.5 h-full min-h-[56px]">
      {c.tronc_commun_groupe && (
        <div className="absolute top-0.5 right-0.5 text-[9px] bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 px-1 py-0.5 rounded inline-flex items-center gap-0.5">
          <Layers size={8} /> TC
        </div>
      )}
      <div className="text-[11px] font-semibold text-gray-900 dark:text-white leading-tight">
        {c.libelle_matiere}
      </div>
      {c.formateur_nom && (
        <div className="text-[10px] text-purple-600 dark:text-purple-300 leading-tight mt-0.5 truncate">
          {c.formateur_nom}
        </div>
      )}
      {c.salle && (
        <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{c.salle}</div>
      )}
      {hover && (
        <div className="absolute bottom-0 right-0 flex bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow-md">
          <button onClick={onEdit} title="Modifier"
            className="p-1 text-gray-700 dark:text-gray-300 hover:text-blue-500"><Pencil size={11} /></button>
          <button onClick={onCopier} title="Copier vers d'autres classes"
            className="p-1 text-gray-700 dark:text-gray-300 hover:text-blue-500"><Copy size={11} /></button>
          <button onClick={onDeplacer} title="Déplacer"
            className="p-1 text-gray-700 dark:text-gray-300 hover:text-blue-500"><Move size={11} /></button>
          <button onClick={onDelete} title="Supprimer"
            className="p-1 text-gray-700 dark:text-gray-300 hover:text-red-500"><Trash2 size={11} /></button>
        </div>
      )}
    </div>
  );
}

function VueLibreParJour({ creneaux, onEdit, onCopier, onDeplacer, onDelete }: {
  creneaux: any[];
  onEdit: (c: any) => void;
  onCopier: (c: any) => void;
  onDeplacer: (c: any) => void;
  onDelete: (c: any) => void;
}) {
  const parJour = useMemo(() => {
    const m: Record<string, any[]> = {};
    JOURS.forEach(j => { m[j] = []; });
    for (const c of creneaux) {
      if (m[c.jour]) m[c.jour].push(c);
    }
    for (const arr of Object.values(m)) {
      arr.sort((a, b) => a.heure_debut.localeCompare(b.heure_debut) || a.classe.localeCompare(b.classe));
    }
    return m;
  }, [creneaux]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {JOURS.map(j => (
        <div key={j} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3">
          <h3 className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-2">
            {JOURS_LABEL[j]}{parJour[j].length > 0 && ` (${parJour[j].length})`}
          </h3>
          {parJour[j].length === 0 ? (
            <div className="text-xs text-gray-400 dark:text-gray-600 italic py-2">Aucun créneau</div>
          ) : (
            <div className="space-y-1.5">
              {parJour[j].map(c => (
                <div key={c.id} className="group bg-gray-50 dark:bg-gray-900/50 rounded p-2 hover:shadow-sm transition">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <div className="text-[11px] font-semibold text-blue-600 dark:text-blue-400 font-mono">
                      {c.heure_debut}–{c.heure_fin}
                    </div>
                    {c.tronc_commun_groupe && (
                      <span className="text-[9px] bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 px-1 py-0.5 rounded inline-flex items-center gap-0.5">
                        <Layers size={8} /> TC
                      </span>
                    )}
                  </div>
                  <div className="text-xs font-medium text-gray-900 dark:text-white">{c.libelle_matiere}</div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400">{c.classe}{c.salle && ` • ${c.salle}`}</div>
                  {c.formateur_nom && (
                    <div className="text-[11px] text-purple-500 dark:text-purple-400 mt-0.5">{c.formateur_nom}</div>
                  )}
                  <div className="opacity-0 group-hover:opacity-100 mt-1 flex gap-1 transition">
                    <button onClick={() => onEdit(c)} className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"><Pencil size={9} /></button>
                    <button onClick={() => onCopier(c)} className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"><Copy size={9} /></button>
                    <button onClick={() => onDeplacer(c)} className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"><Move size={9} /></button>
                    <button onClick={() => onDelete(c)} className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-red-500 rounded hover:bg-red-100 dark:hover:bg-red-900/30"><Trash2 size={9} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
